import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './Customers.css';
import { useToast, useConfirm } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';

const LEGAL_ENTITIES_PAK = [
  'Sole Proprietor', 'Partnership', 'Private Limited (Pvt. Ltd.)',
  'Public Limited', 'NGO / Non-Profit', 'Other',
];
const LEGAL_ENTITIES_UAE = [
  'Sole Proprietor', 'LLC', 'Free Zone Company',
  'Branch', 'Civil Company', 'Partnership', 'Other',
];
const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Cheque', 'Online Transfer', 'Other'];


const blankForm = (type = 'uae') => ({
  customer_type: type,
  // Legal
  business_name: '',
  legal_entity: '',
  ntn: '',
  ntn_doc_url: '',
  gst: '',
  gst_doc_url: '',
  license_number: '',
  license_doc_url: '',
  vat: '',
  vat_doc_url: '',
  // Personal + Address
  representative_name: '',
  contact_number_1: '',
  contact_number_2: '',
  email: '',
  eid_passport: '',
  eid_doc_url: '',
  cnic: '',
  id_front_url: '',
  id_back_url: '',
  region: '',
  territory: '',
  landmark: '',
  address_phone: '',
  address_email: '',
  website: '',
  // Financial
  payment_method: '',
  payment_terms: '',
  bank_name: '',
  account_number: '',
  // Partners
  partners: [],
});

function Customers() {
  const { currentUser, login } = useAuth();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(blankForm());
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);
  const { showToast, ToastComponent } = useToast();
  const { showConfirm, ConfirmComponent } = useConfirm();

  const handleLogin = async () => {
    setLoginLoading(true);
    setAuthError('');
    const result = await login(loginData.username, loginData.password);
    if (!result.success) {
      setAuthError('Invalid username or password.');
    }
    setLoginLoading(false);
  };

  const fetchCustomers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error) setCustomers(data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser) fetchCustomers();
  }, [currentUser]);

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Generic doc/image upload — slotKey is the formData field name
  const handleDocUpload = async (file, slotKey) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [slotKey]: true }));
    const ext = file.name.split('.').pop();
    const fileName = `customer-${Date.now()}-${slotKey}.${ext}`;
    const { error } = await supabase.storage
      .from('customer-ids')
      .upload(fileName, file, { upsert: true });
    if (error) {
      showToast('Upload failed. Please try again.', 'error');
      setUploading(prev => ({ ...prev, [slotKey]: false }));
      return;
    }
    const { data } = supabase.storage.from('customer-ids').getPublicUrl(fileName);
    set(slotKey, data.publicUrl);
    setUploading(prev => ({ ...prev, [slotKey]: false }));
  };

  // Partners
  const addPartner = () => {
    set('partners', [...(formData.partners || []), { name: '', id_number: '', id_front_url: '', id_back_url: '' }]);
  };

  const handlePartnerDocUpload = async (file, partnerIndex, side) => {
    if (!file) return;
    const slotKey = `partner-${partnerIndex}-${side}`;
    setUploading(prev => ({ ...prev, [slotKey]: true }));
    const ext = file.name.split('.').pop();
    const fileName = `partner-${Date.now()}-${partnerIndex}-${side}.${ext}`;
    const { error } = await supabase.storage.from('customer-ids').upload(fileName, file, { upsert: true });
    if (error) {
      showToast('Upload failed. Please try again.', 'error');
      setUploading(prev => ({ ...prev, [slotKey]: false }));
      return;
    }
    const { data } = supabase.storage.from('customer-ids').getPublicUrl(fileName);
    updatePartner(partnerIndex, side === 'front' ? 'id_front_url' : 'id_back_url', data.publicUrl);
    setUploading(prev => ({ ...prev, [slotKey]: false }));
  };
  const updatePartner = (i, field, value) => {
    const updated = [...(formData.partners || [])];
    updated[i] = { ...updated[i], [field]: value };
    set('partners', updated);
  };
  const removePartner = (i) => {
    set('partners', (formData.partners || []).filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    if (!formData.business_name.trim() || !formData.representative_name.trim()) {
      showToast('Business name and representative name are required.', 'warning');
      return;
    }
    setSaving(true);
    if (view === 'add') {
      const { data: custId, error: idError } = await supabase.rpc('next_customer_id', { p_type: formData.customer_type });
      if (idError) {
        showToast('Failed to generate customer ID. Please try again.', 'error');
        setSaving(false);
        return;
      }
      const customer_id = custId;
      const { error } = await supabase.from('customers').insert([{ ...formData, customer_id }]);
      if (error) {
        showToast('Error saving customer. Please try again.', 'error');
      } else {
        showToast('Customer saved successfully!', 'success');
        await fetchCustomers();
        setView('list');
        setFormData(blankForm());
      }
    } else if (view === 'edit') {
      const { error } = await supabase.from('customers').update(formData).eq('id', selected.id);
      if (error) {
        showToast('Error updating customer. Please try again.', 'error');
      } else {
        showToast('Customer updated successfully!', 'success');
        await fetchCustomers();
        setView('list');
        setSelected(null);
      }
    }
    setSaving(false);
  };

  const handleEdit = (c) => {
    setSelected(c);
    setFormData({ ...blankForm(c.customer_type), ...c, partners: c.partners || [] });
    setView('edit');
  };

  const handleDelete = (id) => {
    showConfirm({
      title: 'Delete Customer',
      message: 'Are you sure you want to delete this customer? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        await supabase.from('customers').delete().eq('id', id);
        await fetchCustomers();
        setView('list');
        setSelected(null);
        showToast('Customer deleted successfully.', 'success');
      },
    });
  };

  const isPak = formData.customer_type === 'pakistan';
  const legalEntities = isPak ? LEGAL_ENTITIES_PAK : LEGAL_ENTITIES_UAE;

  const filtered = customers.filter(c => {
    const matchType = typeFilter === 'all' || c.customer_type === typeFilter;
    const matchSearch = !search ||
      c.business_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.representative_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.customer_id?.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const pakCount = customers.filter(c => c.customer_type === 'pakistan').length;
  const uaeCount = customers.filter(c => c.customer_type === 'uae').length;

  // ── DOC UPLOAD INLINE COMPONENT ──
  const DocUploadBtn = ({ slotKey, label }) => (
    <span className="doc-upload-inline">
      <input
        id={`doc-${slotKey}`}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        className="doc-file-input"
        onChange={(e) => handleDocUpload(e.target.files[0], slotKey)}
      />
      <label htmlFor={`doc-${slotKey}`} className={`doc-upload-icon-btn ${formData[slotKey] ? 'uploaded' : ''}`}
        title={formData[slotKey] ? `${label} uploaded ✓` : `Upload ${label}`}>
        {uploading[slotKey] ? '⏳' : formData[slotKey] ? '✅' : '📎'}
      </label>
    </span>
  );

  // ── LOGIN ──
  if (!currentUser) {
    return (
      <div className="customers-login">
        <div className="login-card">
          <div className="login-icon">👥</div>
          <p className="login-brand">Al Mushtaraka Trading Co.</p>
          <h2>CUSTOMER MODULE</h2>
          <p>Enter your credentials to access customer records</p>
          <div className="login-input-wrapper">
            <span className="login-input-icon">👤</span>
            <input
              type="text"
              placeholder="Username"
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              autoFocus
            />
          </div>
          <div className="login-input-wrapper">
            <span className="login-input-icon">🔑</span>
            <input
              type="password"
              placeholder="Password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>
          {authError && <p className="login-error">⚠️ {authError}</p>}
          <button onClick={handleLogin} disabled={loginLoading}>
            {loginLoading ? 'Checking...' : 'Access Customers →'}
          </button>
        </div>
      </div>
    );
  }

  // ── ADD / EDIT FORM ──
  if (view === 'add' || view === 'edit') {
    return (
      <div className="customers-page">
        {ToastComponent}
        {ConfirmComponent}
        <div className="customers-header">
          <button className="btn-back-customers" onClick={() => { setView('list'); setFormData(blankForm()); setSelected(null); }}>
            ← Back to Customers
          </button>
          <h1>{view === 'add' ? 'Add New Customer' : 'Edit Customer'}</h1>
        </div>

        <div className="customer-form-card">

          {/* Customer Type Toggle */}
          <div className="cust-type-toggle-section">
            <p className="cust-type-label">Customer Region</p>
            <div className="cust-type-toggle">
              <button
                className={`cust-type-btn ${formData.customer_type === 'pakistan' ? 'active pak' : ''}`}
                onClick={() => setFormData({ ...blankForm('pakistan') })}
                type="button"
              >
                🇵🇰 Pakistan
              </button>
              <button
                className={`cust-type-btn ${formData.customer_type === 'uae' ? 'active uae' : ''}`}
                onClick={() => setFormData({ ...blankForm('uae') })}
                type="button"
              >
                🇦🇪 UAE
              </button>
            </div>
            {isPak && <div className="cust-region-banner pak-banner">🇵🇰 <strong>PAKISTANI USER</strong> — NTN / CNIC fields apply</div>}
            {!isPak && <div className="cust-region-banner uae-banner">🇦🇪 <strong>UAE USER</strong> — Trade License / EID fields apply</div>}
          </div>

          {/* ── SECTION 1: LEGAL INFORMATION ── */}
          <div className="cust-form-section">
            <div className="cust-section-header">
              <span className="cust-section-number">1</span>
              <h3>Legal Information</h3>
            </div>
            <div className="cust-form-grid">
              <div className="form-group full-width">
                <label>Business Name *</label>
                <input type="text" placeholder={isPak ? 'e.g. PAK TOWER MECHANIC' : 'e.g. AL NOOR TRADING LLC'}
                  value={formData.business_name} onChange={(e) => set('business_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Legal Entity</label>
                <select value={formData.legal_entity} onChange={(e) => set('legal_entity', e.target.value)}>
                  <option value="">Select entity type</option>
                  {legalEntities.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>

              {isPak ? (
                <>
                  <div className="form-group">
                    <label>NTN (National Tax Number)</label>
                    <div className="input-with-upload">
                      <input type="text" placeholder="e.g. 1234567-8"
                        value={formData.ntn} onChange={(e) => set('ntn', e.target.value)} />
                      <DocUploadBtn slotKey="ntn_doc_url" label="NTN Document" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>GST Number</label>
                    <div className="input-with-upload">
                      <input type="text" placeholder="e.g. 03-00-6703-003-82"
                        value={formData.gst} onChange={(e) => set('gst', e.target.value)} />
                      <DocUploadBtn slotKey="gst_doc_url" label="GST Document" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>License Number</label>
                    <div className="input-with-upload">
                      <input type="text" placeholder="e.g. CN-6042889"
                        value={formData.license_number} onChange={(e) => set('license_number', e.target.value)} />
                      <DocUploadBtn slotKey="license_doc_url" label="License Document" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>VAT Number</label>
                    <div className="input-with-upload">
                      <input type="text" placeholder="e.g. 100123456789003"
                        value={formData.vat} onChange={(e) => set('vat', e.target.value)} />
                      <DocUploadBtn slotKey="vat_doc_url" label="VAT Document" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── SECTION 2: PERSONAL INFORMATION + ADDRESS ── */}
          <div className="cust-form-section">
            <div className="cust-section-header">
              <span className="cust-section-number">2</span>
              <h3>Personal Information</h3>
            </div>
            <div className="cust-form-grid">
              <div className="form-group full-width">
                <label>Representative Name *</label>
                <input type="text" placeholder="Full name of representative"
                  value={formData.representative_name} onChange={(e) => set('representative_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Contact Number 1 *</label>
                <input type="text" placeholder={isPak ? 'e.g. +92 300 1234567' : 'e.g. +971 52 168 2976'}
                  value={formData.contact_number_1} onChange={(e) => set('contact_number_1', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Contact Number 2 <span className="optional-tag">(optional)</span></label>
                <input type="text" placeholder="Secondary number"
                  value={formData.contact_number_2} onChange={(e) => set('contact_number_2', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" placeholder="representative@company.com"
                  value={formData.email} onChange={(e) => set('email', e.target.value)} />
              </div>

              {/* UAE: EID/Passport with doc upload */}
              {!isPak && (
                <div className="form-group">
                  <label>EID / Passport Number</label>
                  <input type="text" placeholder="e.g. 784-1995-7170468-5"
                    value={formData.eid_passport} onChange={(e) => set('eid_passport', e.target.value)} />
                </div>
              )}

              {/* Pakistan: CNIC number + front/back inline */}
              {isPak && (
                <div className="form-group full-width">
                  <label>CNIC Number</label>
                  <div className="cnic-row">
                    <input type="text" placeholder="e.g. 35202-1234567-1"
                      value={formData.cnic} onChange={(e) => set('cnic', e.target.value)} />
                    <div className="cnic-uploads">
                      <input id="cnic-front" type="file" accept="image/jpeg,image/png" className="doc-file-input"
                        onChange={(e) => handleDocUpload(e.target.files[0], 'id_front_url')} />
                      <label htmlFor="cnic-front" className={`cnic-upload-btn ${formData.id_front_url ? 'uploaded' : ''}`}
                        title={formData.id_front_url ? 'Front uploaded ✓' : 'Upload CNIC Front'}>
                        {uploading['id_front_url'] ? '⏳' : formData.id_front_url ? '✅' : '📎'} Front
                      </label>
                      <input id="cnic-back" type="file" accept="image/jpeg,image/png" className="doc-file-input"
                        onChange={(e) => handleDocUpload(e.target.files[0], 'id_back_url')} />
                      <label htmlFor="cnic-back" className={`cnic-upload-btn ${formData.id_back_url ? 'uploaded' : ''}`}
                        title={formData.id_back_url ? 'Back uploaded ✓' : 'Upload CNIC Back'}>
                        {uploading['id_back_url'] ? '⏳' : formData.id_back_url ? '✅' : '📎'} Back
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* UAE: EID front/back inline under EID field */}
              {!isPak && (
                <div className="form-group full-width">
                  <label>Emirates ID / Passport Images</label>
                  <div className="cnic-row no-text">
                    <div className="cnic-uploads">
                      <input id="eid-front" type="file" accept="image/jpeg,image/png" className="doc-file-input"
                        onChange={(e) => handleDocUpload(e.target.files[0], 'id_front_url')} />
                      <label htmlFor="eid-front" className={`cnic-upload-btn wide ${formData.id_front_url ? 'uploaded' : ''}`}
                        title={formData.id_front_url ? 'Front uploaded ✓' : 'Upload Front'}>
                        {uploading['id_front_url'] ? '⏳ Uploading...' : formData.id_front_url ? '✅ Front Uploaded' : '📎 Upload Front'}
                      </label>
                      <input id="eid-back" type="file" accept="image/jpeg,image/png" className="doc-file-input"
                        onChange={(e) => handleDocUpload(e.target.files[0], 'id_back_url')} />
                      <label htmlFor="eid-back" className={`cnic-upload-btn wide ${formData.id_back_url ? 'uploaded' : ''}`}
                        title={formData.id_back_url ? 'Back uploaded ✓' : 'Upload Back'}>
                        {uploading['id_back_url'] ? '⏳ Uploading...' : formData.id_back_url ? '✅ Back Uploaded' : '📎 Upload Back'}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Address fields merged in */}
              <div className="form-group cust-subsection-label full-width"><span>Address</span></div>
              <div className="form-group">
                <label>Region</label>
                <input type="text" placeholder={isPak ? 'e.g. Punjab' : 'e.g. Fujairah'}
                  value={formData.region} onChange={(e) => set('region', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Territory / City</label>
                <input type="text" placeholder={isPak ? 'e.g. Lahore' : 'e.g. Dibba Al Fujairah'}
                  value={formData.territory} onChange={(e) => set('territory', e.target.value)} />
              </div>
              <div className="form-group full-width">
                <label>Near Landmark</label>
                <input type="text" placeholder="e.g. Near Industrial Area Gate 3"
                  value={formData.landmark} onChange={(e) => set('landmark', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Office Phone</label>
                <input type="text" placeholder="Office landline"
                  value={formData.address_phone} onChange={(e) => set('address_phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Office Email</label>
                <input type="email" placeholder="info@company.com"
                  value={formData.address_email} onChange={(e) => set('address_email', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input type="text" placeholder="www.company.com"
                  value={formData.website} onChange={(e) => set('website', e.target.value)} />
              </div>
            </div>
          </div>

          {/* ── SECTION 3: FINANCIAL DETAILS ── */}
          <div className="cust-form-section">
            <div className="cust-section-header">
              <span className="cust-section-number">3</span>
              <h3>Financial Details</h3>
            </div>
            <div className="cust-form-grid">
              <div className="form-group">
                <label>Payment Method</label>
                <select value={formData.payment_method} onChange={(e) => set('payment_method', e.target.value)}>
                  <option value="">Select payment method</option>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Payment Terms</label>
                <input type="text" placeholder="e.g. Net 30, COD, Advance"
                  value={formData.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                <input type="text" placeholder={isPak ? 'e.g. HBL / MCB / UBL' : 'e.g. Emirates NBD'}
                  value={formData.bank_name} onChange={(e) => set('bank_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Account Number / IBAN</label>
                <input type="text" placeholder="e.g. PK36SCBL0000001123456702"
                  value={formData.account_number} onChange={(e) => set('account_number', e.target.value)} />
              </div>
            </div>
          </div>

          {/* ── SECTION 4: PARTNERS ── */}
          <div className="cust-form-section">
            <div className="cust-section-header">
              <span className="cust-section-number">4</span>
              <h3>Partners / Co-Buyers</h3>
              <button type="button" className="btn-add-partner" onClick={addPartner}>+ Add Partner</button>
            </div>
            {(!formData.partners || formData.partners.length === 0) ? (
              <p className="partners-empty">No partners added. Click "+ Add Partner" to add co-buyers for this customer.</p>
            ) : (
              <div className="partners-list">
                {formData.partners.map((p, i) => (
                  <div className="partner-row" key={i}>
                    <span className="partner-num">Partner {i + 1}</span>
                    <div className="form-group">
                      <label>Full Name</label>
                      <input type="text" placeholder="Partner's full name"
                        value={p.name} onChange={(e) => updatePartner(i, 'name', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>{isPak ? 'CNIC / Passport' : 'EID / Passport Number'}</label>
                      <div className="input-with-upload">
                        <input type="text" placeholder={isPak ? 'e.g. 35202-1234567-1' : 'e.g. 784-1995-7170468-5'}
                          value={p.id_number} onChange={(e) => updatePartner(i, 'id_number', e.target.value)} />
                        {/* Front upload */}
                        <input id={`partner-${i}-front`} type="file" accept="image/jpeg,image/png" className="doc-file-input"
                          onChange={(e) => handlePartnerDocUpload(e.target.files[0], i, 'front')} />
                        <label htmlFor={`partner-${i}-front`}
                          className={`doc-upload-icon-btn ${p.id_front_url ? 'uploaded' : ''}`}
                          title={p.id_front_url ? 'Front uploaded ✓' : `Upload ${isPak ? 'CNIC' : 'EID'} Front`}>
                          {uploading[`partner-${i}-front`] ? '⏳' : p.id_front_url ? '✅' : '📎'}
                        </label>
                        {/* Back upload */}
                        <input id={`partner-${i}-back`} type="file" accept="image/jpeg,image/png" className="doc-file-input"
                          onChange={(e) => handlePartnerDocUpload(e.target.files[0], i, 'back')} />
                        <label htmlFor={`partner-${i}-back`}
                          className={`doc-upload-icon-btn ${p.id_back_url ? 'uploaded' : ''}`}
                          title={p.id_back_url ? 'Back uploaded ✓' : `Upload ${isPak ? 'CNIC' : 'EID'} Back`}>
                          {uploading[`partner-${i}-back`] ? '⏳' : p.id_back_url ? '✅' : '📎'}
                        </label>
                      </div>
                    </div>
                    <button type="button" className="btn-remove-partner" onClick={() => removePartner(i)}>✕ Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setView('list'); setFormData(blankForm()); setSelected(null); }}>
              Cancel
            </button>
            <button className="btn-save-customer" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : view === 'add' ? '+ Save Customer' : '✓ Update Customer'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DETAIL VIEW ──
  if (view === 'detail' && selected) {
    const isPakDetail = selected.customer_type === 'pakistan';
    const partners = selected.partners || [];
    return (
      <div className="customers-page">
        {ToastComponent}
        {ConfirmComponent}
        <div className="customers-header">
          <button className="btn-back-customers" onClick={() => { setView('list'); setSelected(null); }}>
            ← Back to Customers
          </button>
          <div className="detail-header-actions">
            <button className="btn-edit-customer" onClick={() => handleEdit(selected)}>✏️ Edit</button>
            <button className="btn-delete-customer" onClick={() => handleDelete(selected.id)}>Delete</button>
          </div>
        </div>

        <div className="customer-detail-card">
          {/* Top — no customer ID here */}
          <div className="customer-detail-top">
            <div className={`customer-avatar ${isPakDetail ? 'pak-avatar' : 'uae-avatar'}`}>
              {selected.business_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="detail-top-meta">
                <span className={`cust-type-badge ${isPakDetail ? 'pak' : 'uae'}`}>
                  {isPakDetail ? '🇵🇰 Pakistan' : '🇦🇪 UAE'}
                </span>
              </div>
              <h2>{selected.business_name}</h2>
              <p className="detail-legal-entity">{selected.legal_entity || '—'}</p>
            </div>
          </div>

          {/* ── 1. Legal Information ── */}
          <div className="detail-section-block">
            <h4>Legal Information</h4>
            <div className="customer-detail-grid">
              <div className="detail-item">
                <label>Business Name</label>
                <p>{selected.business_name || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Legal Entity</label>
                <p>{selected.legal_entity || '—'}</p>
              </div>
              {isPakDetail ? (
                <>
                  <div className="detail-item">
                    <label>NTN</label>
                    <p>{selected.ntn || '—'}{selected.ntn_doc_url && <a href={selected.ntn_doc_url} target="_blank" rel="noreferrer" className="doc-link">📎 View</a>}</p>
                  </div>
                  <div className="detail-item">
                    <label>GST</label>
                    <p>{selected.gst || '—'}{selected.gst_doc_url && <a href={selected.gst_doc_url} target="_blank" rel="noreferrer" className="doc-link">📎 View</a>}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-item">
                    <label>License Number</label>
                    <p>{selected.license_number || '—'}{selected.license_doc_url && <a href={selected.license_doc_url} target="_blank" rel="noreferrer" className="doc-link">📎 View</a>}</p>
                  </div>
                  <div className="detail-item">
                    <label>VAT Number</label>
                    <p>{selected.vat || '—'}{selected.vat_doc_url && <a href={selected.vat_doc_url} target="_blank" rel="noreferrer" className="doc-link">📎 View</a>}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── 2. Personal Information (+ Address) ── */}
          <div className="detail-section-block">
            <h4>Personal Information</h4>
            <div className="customer-detail-grid">
              <div className="detail-item">
                <label>Representative Name</label>
                <p>{selected.representative_name || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Contact Number 1</label>
                <p>{selected.contact_number_1 || '—'}</p>
              </div>
              {selected.contact_number_2 && (
                <div className="detail-item">
                  <label>Contact Number 2</label>
                  <p>{selected.contact_number_2}</p>
                </div>
              )}
              <div className="detail-item">
                <label>Email</label>
                <p>{selected.email || '—'}</p>
              </div>
              {isPakDetail ? (
                <div className="detail-item">
                  <label>CNIC</label>
                  <p>{selected.cnic || '—'}</p>
                </div>
              ) : (
                <div className="detail-item">
                  <label>EID / Passport</label>
                  <p>{selected.eid_passport || '—'}{selected.eid_doc_url && <a href={selected.eid_doc_url} target="_blank" rel="noreferrer" className="doc-link">📎 View</a>}</p>
                </div>
              )}
              {/* Address sub-section */}
              <div className="detail-item full-width detail-subsection-label"><span>Address</span></div>
              <div className="detail-item">
                <label>Region</label>
                <p>{selected.region || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Territory / City</label>
                <p>{selected.territory || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Near Landmark</label>
                <p>{selected.landmark || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Office Phone</label>
                <p>{selected.address_phone || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Office Email</label>
                <p>{selected.address_email || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Website</label>
                <p>{selected.website || '—'}</p>
              </div>
            </div>

            {/* CNIC / EID images inside personal block */}
            {(selected.id_front_url || selected.id_back_url) && (
              <div className="detail-id-images">
                <p className="detail-id-label">{isPakDetail ? 'CNIC Images' : 'Emirates ID / Passport Images'}</p>
                <div className="id-images-row">
                  {selected.id_front_url && (
                    <div className="id-image-box">
                      <span>FRONT</span>
                      <img src={selected.id_front_url} alt="ID Front" />
                    </div>
                  )}
                  {selected.id_back_url && (
                    <div className="id-image-box">
                      <span>BACK</span>
                      <img src={selected.id_back_url} alt="ID Back" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── 3. Financial Details ── */}
          <div className="detail-section-block">
            <h4>Financial Details</h4>
            <div className="customer-detail-grid">
              <div className="detail-item">
                <label>Payment Method</label>
                <p>{selected.payment_method || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Payment Terms</label>
                <p>{selected.payment_terms || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Bank Name</label>
                <p>{selected.bank_name || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Account / IBAN</label>
                <p>{selected.account_number || '—'}</p>
              </div>
              <div className="detail-item">
                <label>Customer Since</label>
                <p>{new Date(selected.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              </div>
            </div>
          </div>

          {/* ── 4. Partners ── */}
          {partners.length > 0 && (
            <div className="detail-section-block">
              <h4>Partners / Co-Buyers</h4>
              <div className="customer-detail-grid">
                {partners.map((p, i) => (
                  <div className="detail-item partner-detail-item" key={i}>
                    <label>Partner {i + 1}</label>
                    <p><strong>{p.name || '—'}</strong></p>
                    <p className="partner-id-text">{isPakDetail ? 'CNIC' : 'EID'}: {p.id_number || '—'}</p>
                    {(p.id_front_url || p.id_back_url) && (
                      <div className="partner-id-thumbs">
                        {p.id_front_url && (
                          <a href={p.id_front_url} target="_blank" rel="noreferrer">
                            <img src={p.id_front_url} alt="Front" className="partner-id-thumb" />
                            <span>Front</span>
                          </a>
                        )}
                        {p.id_back_url && (
                          <a href={p.id_back_url} target="_blank" rel="noreferrer">
                            <img src={p.id_back_url} alt="Back" className="partner-id-thumb" />
                            <span>Back</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Customer ID at bottom ── */}
          <div className="detail-customer-id-row">
            <span className="detail-cust-id-label">Customer ID</span>
            <span className="customer-id-badge">{selected.customer_id}</span>
          </div>

        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="customers-page">
      {ToastComponent}
      {ConfirmComponent}
      <div className="customers-header">
        <div>
          <h1>Customers</h1>
          <p className="customers-subtitle">
            {customers.length} total · {pakCount} Pakistan · {uaeCount} UAE
          </p>
        </div>
        <button className="btn-add-customer" onClick={() => { setFormData(blankForm()); setView('add'); }}>
          + Add Customer
        </button>
      </div>

      <div className="cust-filter-tabs">
        <button className={`cust-filter-tab ${typeFilter === 'all' ? 'active' : ''}`} onClick={() => setTypeFilter('all')}>All ({customers.length})</button>
        <button className={`cust-filter-tab pak-tab ${typeFilter === 'pakistan' ? 'active pak-active' : ''}`} onClick={() => setTypeFilter('pakistan')}>🇵🇰 Pakistan ({pakCount})</button>
        <button className={`cust-filter-tab uae-tab ${typeFilter === 'uae' ? 'active uae-active' : ''}`} onClick={() => setTypeFilter('uae')}>🇦🇪 UAE ({uaeCount})</button>
      </div>

      <div className="customers-search-bar">
        <span className="search-icon">🔍</span>
        <input type="text" placeholder="Search by business name, representative or customer ID..."
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button className="search-clear" onClick={() => setSearch('')}>✕</button>}
      </div>

      {loading ? (
        <p className="loading-text">Loading customers...</p>
      ) : filtered.length === 0 ? (
        <div className="customers-empty">
          <div className="empty-icon">👥</div>
          <h3>{search ? 'No customers found' : 'No customers yet'}</h3>
          <p>{search ? 'Try a different search term' : 'Add your first customer to get started'}</p>
          {!search && (
            <button className="btn-add-customer" onClick={() => { setFormData(blankForm()); setView('add'); }}>
              + Add First Customer
            </button>
          )}
        </div>
      ) : (
        <div className="customers-grid">
          {filtered.map((customer) => (
            <div className="customer-card" key={customer.id}
              onClick={() => { setSelected(customer); setView('detail'); }}>
              <div className="customer-card-top">
                <div className={`customer-card-avatar ${customer.customer_type === 'pakistan' ? 'pak-avatar' : 'uae-avatar'}`}>
                  {customer.business_name?.charAt(0).toUpperCase()}
                </div>
                <span className={`cust-type-badge ${customer.customer_type === 'pakistan' ? 'pak' : 'uae'}`}>
                  {customer.customer_type === 'pakistan' ? '🇵🇰' : '🇦🇪'}
                </span>
              </div>
              <h3 className="customer-card-company">{customer.business_name}</h3>
              <p className="customer-card-rep">{customer.representative_name}</p>
              <div className="customer-card-footer">
                <span>{customer.contact_number_1 || '—'}</span>
                <span className="customer-card-arrow">→</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Customers;
